import Mediator from 'mediator-js';
import log, { cleanObjectProperties } from 'Utilities/log';

/**
 * When you want to import this, do so as:
 * import * as mediator from 'Utility/mediator'
 * so you can do mediator.subscribe() or mediator.publish() for example
 *
 * https://github.com/ajacksified/Mediator.js
 */

const logEvents = true;
const _m = new Mediator();
const logEvent = (...args) => {
  if (!logEvents) {
    return;
  }
  const [id, ...rest] = args;
  if (log.getLogLevel() !== 'DEBUG') {
    log.groupAndTrace(id);
    return;
  }
  const cleanedArgs = rest.map(v => {
    if (typeof v === 'object') {
      return cleanObjectProperties(v);
    }
    return v;
  });
  log.groupAndTrace(id, ...cleanedArgs);
};
export const subscribe = _m.subscribe.bind(_m);
export const once = _m.once.bind(_m);
export const remove = _m.remove.bind(_m);
export const publish = (...args) => {
  // want to know who published the event
  logEvent(...args);
  const [id, ...originalArguments] = args;
  return new Promise((resolve, reject) => {
    try {
      _m.publish(id, ...originalArguments);
      resolve();
    } catch (ex) {
      reject(ex);
    }
  });
};
export const publishAndWait = (id, ...args) => {
  // want to know who published the event
  logEvent(id, ...args);
  _m.publish(id, ...args);
};
export const get = id => {
  // want to know who published the event
  logEvent(id);
  const data = {};
  _m.publish(id, data);
  return data;
};

// you can optionally supply typing automatically for certain mediator requests, here's a commented example
// export function request<T, K>(id: 'api:invoke', request?: MediatorRequest<K>): Promise<LVApiResponse<T>>;

export function request(id, request) {
  if (typeof id === 'string') {
    if (request) {
      request._topicId = id;
    } else {
      request = request || {
        _topicId: id
      };
    }
  } else {
    request = id || {};
  }
  if (!request._topicId) {
    throw new Error('missing request._topicId');
  }

  // want to know who published the event
  logEvent(request._topicId, request);
  const _request = request;
  return new Promise((resolve, reject) => {
    try {
      _m.publish(_request._topicId, _request);
      if (!_request.response) {
        reject(new Error('missing Response'));
        return;
      }
      _request.response.then(val => {
        resolve(val);
      }).catch(ex => {
        reject(ex);
      });
    } catch (ex) {
      reject(ex);
    }
  });
}
export default {
  get,
  once,
  publish,
  publishAndWait,
  remove,
  request,
  subscribe
};